/* Read Customily capabilities from its live provider model.
 * Product handles, option IDs and property names are deliberately excluded.
 */
(() => {
  const optionSelector = '.customily_sets-panel .customily_option';
  const panelCallbacks = new Set();
  let panel;
  let panelObserver;
  let discoveryTimer;
  let panelFrame;
  let fieldsCache;
  let cacheFrame;
  const invalidateFields = () => { fieldsCache = null; };
  const schedulePanel = () => {
    invalidateFields();
    if (panelFrame) return;
    panelFrame = requestAnimationFrame(() => {
      panelFrame = null;
      if (!panel?.isConnected) discoverPanel();
      if (!panel) return;
      scan(panel);
      for (const callback of panelCallbacks) callback();
    });
  };
  const discoverPanel = () => {
    panelObserver?.disconnect();
    clearTimeout(discoveryTimer);
    invalidateFields();
    panel = document.querySelector('.customily_sets-panel');
    panelObserver = new MutationObserver((records) => {
      if (!panel?.isConnected) {
        const next = document.querySelector('.customily_sets-panel');
        if (next !== panel) { discoverPanel(); return; }
        if (!next) return;
      }
      // Some theme mirrors re-assert an identical class/style value. Ignore
      // those records so scheduler consumers cannot create an idle rAF loop.
      if (records.length && records.every((record) => record.type === 'attributes'
        && record.oldValue === record.target.getAttribute(record.attributeName))) return;
      schedulePanel();
    });
    if (panel) {
      // Parent childList detects provider panel replacement without observing
      // every unrelated mutation elsewhere in the document.
      if (panel.parentElement) panelObserver.observe(panel.parentElement, { childList: true });
      panelObserver.observe(panel, { childList: true, subtree: true,
        attributes: true, attributeOldValue: true, attributeFilter: ['style', 'class', 'hidden'] });
      document.dispatchEvent(new CustomEvent('hanoss:customily-panel-ready'));
      schedulePanel();
    } else {
      panelObserver.observe(document.body, { childList: true, subtree: true });
      discoveryTimer = setTimeout(() => panelObserver.disconnect(), 15000);
    }
  };
  const onPanelChange = (callback) => {
    panelCallbacks.add(callback);
    schedulePanel();
    return () => panelCallbacks.delete(callback);
  };

  // Both preview shells share one bounded discovery observer. Once the
  // provider modal exists, unrelated body churn no longer wakes its clients.
  const previewCallbacks = new Set();
  let previewObserver;
  let previewRoot;
  let previewFrame;
  const observePreviewRoot = () => {
    previewObserver?.disconnect();
    if (!previewCallbacks.size) { previewObserver = null; return; }
    previewRoot = document.querySelector('.customily-modal-preview-only');
    previewObserver = new MutationObserver(schedulePreview);
    if (previewRoot) {
      previewObserver.observe(previewRoot, { childList: true, subtree: true,
        attributes: true, attributeFilter: ['style', 'src', 'class'] });
      if (previewRoot.parentElement) previewObserver.observe(previewRoot.parentElement, { childList: true });
    } else {
      previewObserver.observe(document.body, { childList: true, subtree: true });
    }
  };
  const schedulePreview = () => {
    if (previewFrame || !previewCallbacks.size) return;
    previewFrame = requestAnimationFrame(() => {
      previewFrame = null;
      const next = document.querySelector('.customily-modal-preview-only');
      if (next !== previewRoot) observePreviewRoot();
      for (const client of [...previewCallbacks]) {
        if (client.callback()) client.disconnect();
      }
    });
  };
  const watchPreview = (callback) => {
    const client = { callback, disconnect: () => {
      clearTimeout(client.timer);
      previewCallbacks.delete(client);
      if (!previewCallbacks.size) {
        previewObserver?.disconnect();
        previewObserver = null;
        if (previewFrame) cancelAnimationFrame(previewFrame);
        previewFrame = null;
      }
    } };
    previewCallbacks.add(client);
    client.timer = setTimeout(client.disconnect, 5000);
    observePreviewRoot();
    schedulePreview();
    return client;
  };

  // Customily removes Vue's private DOM references after hydrating a stored
  // customization. Keep the provider model discovered during the initial
  // config scan so capability detection survives that provider re-render.
  const optionModelCache = new WeakMap();
  const optionModelByControls = new Map();
  const optionControlKey = (option) => [...(option?.querySelectorAll?.('[name^="properties["]') || [])]
    .map((control) => `${control.type}:${control.name}`)
    .sort()
    .join('\u0000');

  const optionModel = (option) => {
    const live = option?.__vue__?.option
    || option?.__vueParentComponent?.proxy?.option
    || option?.__vueParentComponent?.ctx?.option;
    const key = optionControlKey(option);
    if (live) {
      optionModelCache.set(option, live);
      if (key) optionModelByControls.set(key, live);
      return live;
    }
    const cached = optionModelCache.get(option) || (key ? optionModelByControls.get(key) : null);
    if (cached && optionsInstance?.sets) {
      return optionsInstance.sets.flatMap((set) => set.options || []).find((model) =>
        String(model.setId) === String(cached.setId) && String(model.sort_id) === String(cached.sort_id)) || null;
    }
    return cached || null;
  };

  let optionsInstance = null;
  const supportsRestore = (vm) => vm && !vm._isDestroyed
    && typeof vm.setOptionsCustomizationData === 'function'
    && typeof vm.getSelectedOptions === 'function';
  const getOptionsInstance = () => {
    for (const element of document.querySelectorAll(optionSelector)) {
      let vm = element.__vue__ || element.__vueParentComponent?.proxy;
      while (vm) {
        if (supportsRestore(vm)) {
          if (optionsInstance !== vm) optionModelByControls.clear();
          optionsInstance = vm;
          return vm;
        }
        vm = vm.$parent;
      }
    }
    if (!supportsRestore(optionsInstance)) optionsInstance = null;
    return optionsInstance;
  };
  const selectedOptions = () => {
    const vm = getOptionsInstance();
    return vm ? { status: 'supported', items: vm.getSelectedOptions() } : { status: 'unsupported', items: [] };
  };
  const applyOptionValues = async (items) => {
    const vm = getOptionsInstance();
    if (!vm) return { status: 'unsupported' };
    await vm.setOptionsCustomizationData(items, true, true);
    await vm.$nextTick();
    return { status: 'applied', instance: vm };
  };
  const models = () => (getOptionsInstance()?.sets || []).flatMap((set) =>
    (set.options || []).map((model) => ({ model, setId: set.id, setSortId: set.sort_id })));
  const sameValue = (a, b) => JSON.stringify(a ?? '') === JSON.stringify(b ?? '');
  const canonicalValue = (model, value) => {
    if (!Array.isArray(model.values) || !model.values.length) return value;
    const match = model.values.find((choice) => sameValue(choice.value, value))
      || model.values.find((choice) => sameValue(choice.text, value));
    if (!match) throw new Error('A saved choice is no longer available in the product configuration.');
    return match.value;
  };
  const propertyName = (control) => control?.name?.match(/^properties\[(.*)]$/)?.[1];
  const restoreItems = (properties, stored = {}) => {
    const fields = [...document.querySelectorAll(optionSelector)];
    return models().flatMap(({ model, setId, setSortId }) => {
      const element = fields.find((field) => optionModel(field) === model);
      const names = getPropertyControls(element).map(propertyName).filter(Boolean);
      // Conditional controls need not be mounted: Customily uses its configured
      // label for the property name, as observed on the live mounted controls.
      if (!names.length && model.label) names.push(model.label + (model.labelDuplicatePrevention || ''));
      const name = names.find((key) => Object.hasOwn(properties, key));
      const fallback = (stored?.options || []).find((item) =>
        (String(item.setId) === String(setId) || item.setId == null && item.setSortId === setSortId)
        && String(item.sortId) === String(model.sort_id));
      if (!name && !fallback) return [];
      const value = canonicalValue(model, name ? properties[name] : fallback.value);
      return [{ ...(fallback || {}), setId, setSortId, sortId: model.sort_id, type: model.type, value }];
    });
  };
  const modelForItem = (item) => models().find(({ model, setId }) =>
    String(setId) === String(item.setId) && String(model.sort_id) === String(item.sortId))?.model;
  const providerReady = () => {
    const vm = getOptionsInstance();
    return Boolean(vm && vm.loaded === true && vm.isProductLoadComplete === true
      && !vm.isProductLoading && !vm.isCustomizationLoading && !window.engraver?.productLoading);
  };
  // Vue readiness/generation flags are reactive; Fabric rendering is not.
  // A bounded subscription observes both without a permanent polling loop.
  const waitForState = (predicate, { signal, timeout = 60000 } = {}) => new Promise((resolve, reject) => {
    let stopped = false, vm, unwatch, canvas;
    const observer = new MutationObserver(check);
    const finish = (error, value) => {
      if (stopped) return;
      stopped = true;
      clearTimeout(timer);
      observer.disconnect(); unwatch?.();
      canvas?.off?.('after:render', check);
      document.removeEventListener('hanoss:customily-capabilities', check);
      signal?.removeEventListener('abort', abort);
      error ? reject(error) : resolve(value);
    };
    const abort = () => finish(signal?.reason instanceof Error ? signal.reason : new Error('Restore interrupted. Please try again.'));
    function check() {
      if (stopped) return;
      if (signal?.aborted) return abort();
      try {
        const current = getOptionsInstance();
        if (current !== vm) {
          unwatch?.(); vm = current;
          unwatch = vm?.$watch?.(() => [vm.loaded, vm.isProductLoadComplete, vm.isProductLoading,
            vm.isCustomizationLoading, vm.sets], check, { deep: true });
        }
        const nextCanvas = window.engraver?.canvas;
        if (nextCanvas !== canvas) {
          canvas?.off?.('after:render', check); canvas = nextCanvas;
          canvas?.on?.('after:render', check);
        }
        const value = predicate();
        if (value) finish(null, value);
      } catch (error) { finish(error); }
    }
    const timer = setTimeout(() => finish(new Error(getOptionsInstance()
      ? 'Your saved options or artwork could not be restored. Close this editor and try again.'
      : 'This Customily version does not support restoring saved options (unsupported).')), timeout);
    observer.observe(document.body, { childList: true, subtree: true });
    document.addEventListener('hanoss:customily-capabilities', check);
    signal?.addEventListener('abort', abort, { once: true });
    check();
  });
  const textExpectations = (items) => items.flatMap((item) => {
    const model = modelForItem(item);
    if (!model || model.isVisible === false) return [];
    const choice = model.values?.find((value) => sameValue(value.value, item.value));
    return (model.functions || []).filter((fn) => fn.type === 'text').map((fn) => ({
      id: Number(fn.text_id), text: String(choice ? choice.text || choice.value || '' : item.value), model, setId: item.setId,
    }));
  });
  const canvasTextsMatch = (items) => {
    const flatten = (objects) => objects.flatMap((object) => [object, ...flatten(object.getObjects?.() || [])]);
    const objects = flatten(window.engraver?.canvas?.getObjects?.() || []);
    return textExpectations(items).every(({ id, text }) => objects.some((object) =>
      Number(object.id) === id && typeof object.text === 'string' && object.text === text));
  };
  const verifyOptionValues = (items) => {
    const selected = selectedOptions();
    if (selected.status === 'unsupported') return false;
    return items.every((item) => {
      const model = modelForItem(item);
      if (!model || !sameValue(model.type === 'Color Picker' ? model.optionValue : model.value, item.value)) return false;
      if (model.isVisible === false) return true;
      if (!selected.items.some((entry) => String(entry.setId) === String(item.setId)
        && String(entry.sortId) === String(item.sortId) && sameValue(entry.value, item.value))) return false;
      const element = [...document.querySelectorAll(optionSelector)].find((field) => optionModel(field) === model);
      const controls = getPropertyControls(element);
      return controls.some((control) => ['radio', 'checkbox'].includes(control.type)
        ? control.checked && sameValue(control.value, item.value) : sameValue(control.value, item.value));
    });
  };

  const getFunctions = (option) => {
    const functions = optionModel(option)?.functions;
    return Array.isArray(functions)
      ? functions.filter((item) => item && typeof item.type === 'string').map((item) => ({ ...item }))
      : (option?.dataset?.hnCustomilyFunctions || '').split(/\s+/).filter(Boolean).map((type) => ({ type }));
  };

  const getTypes = (option) => [...new Set(getFunctions(option).map((item) => item.type))].sort();

  const getControlType = (option) => {
    const type = optionModel(option)?.type;
    return typeof type === 'string' ? type : option?.dataset?.hnCustomilyControlType || '';
  };

  const getLabel = (option) => {
    const label = optionModel(option)?.label;
    return typeof label === 'string' ? label.trim() : option?.dataset?.hnCustomilyLabel || '';
  };

  const getPropertyControls = (option) => [...(option?.querySelectorAll?.('[name^="properties["]') || [])];

  const getPropertyControl = (option, { checked = false } = {}) => {
    const controls = getPropertyControls(option);
    if (!checked) return controls[0] || null;
    return controls.find((control) => !['radio', 'checkbox'].includes(control.type) || control.checked) || null;
  };

  const findOptions = (type, root = document) => [...root.querySelectorAll(optionSelector)]
    .filter((option) => getTypes(option).includes(type));

  const findControls = (type, root = document) => [...root.querySelectorAll(optionSelector)]
    .filter((option) => getControlType(option) === type);

  const getConfiguredFields = (root = document) => {
    if (root === document && fieldsCache) return fieldsCache;
    const crosswordOption = findOptions('crossword', root)[0] || null;
    const quoteOption = findControls('Dropdown Swatch', root)
      .find((option) => getTypes(option).includes('text') && getPropertyControl(option)) || null;
    const relationshipOption = findControls('Swatch', root)
      .find((option) => getTypes(option).includes('text') && getPropertyControls(option).some((control) => control.type === 'radio')) || null;
    const customTextOption = findControls('Text Input', root)
      .find((option) => option !== crosswordOption && getTypes(option).includes('text') && getPropertyControl(option)) || null;

    const fields = {
      crossword: getPropertyControl(crosswordOption),
      crosswordOption,
      customText: getPropertyControl(customTextOption),
      customTextOption,
      quote: getPropertyControl(quoteOption),
      quoteOption,
      relationship: getPropertyControl(relationshipOption, { checked: true }),
      relationshipOption,
    };
    if (root === document) {
      fieldsCache = fields;
      if (!cacheFrame) cacheFrame = requestAnimationFrame(() => { cacheFrame = null; invalidateFields(); });
    }
    return fields;
  };

  const scan = (root = document) => {
    getOptionsInstance();
    const options = [];
    if (root instanceof Element && root.matches(optionSelector)) options.push(root);
    const descendants = root.querySelectorAll?.(optionSelector) || [];
    options.push(...descendants);

    let changed = false;
    options.forEach((option) => {
      const model = optionModel(option);
      const value = getTypes(option).join(' ');
      const controlType = typeof model?.type === 'string' ? model.type : option.dataset.hnCustomilyControlType || '';
      const label = typeof model?.label === 'string' ? model.label.trim() : option.dataset.hnCustomilyLabel || '';
      if (option.dataset.hnCustomilyFunctions !== value) {
        if (value) option.dataset.hnCustomilyFunctions = value;
        else delete option.dataset.hnCustomilyFunctions;
        changed = true;
      }
      if (option.dataset.hnCustomilyControlType !== controlType) {
        if (controlType) option.dataset.hnCustomilyControlType = controlType;
        else delete option.dataset.hnCustomilyControlType;
        changed = true;
      }
      if (option.dataset.hnCustomilyLabel !== label) {
        if (label) option.dataset.hnCustomilyLabel = label;
        else delete option.dataset.hnCustomilyLabel;
        changed = true;
      }
    });

    if (changed) document.dispatchEvent(new CustomEvent('hanoss:customily-capabilities'));
    return options;
  };

  const api = Object.freeze({
    getOptionsInstance, selectedOptions, applyOptionValues, models, modelForItem, restoreItems,
    providerReady, waitForState, sameValue, canvasTextsMatch, textExpectations, verifyOptionValues,
    findControls,
    findOptions,
    getConfiguredFields,
    getControlType,
    getFunctions,
    getLabel,
    getPropertyControl,
    getPropertyControls,
    getTypes,
    optionModel,
    scan,
    onPanelChange,
    watchPreview,
  });
  window.HanossCustomilyCapabilities = api;

  const init = () => {
    document.body.dataset.hnCustomilyCapabilitiesReady = 'true';
    scan();
    discoverPanel();
  };

  document.addEventListener('shopify:section:load', discoverPanel);
  for (const eventName of ['input', 'change']) document.addEventListener(eventName, (event) => {
    if (event.target?.closest?.('.customily_sets-panel')) schedulePanel();
  }, true);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
